import { Suspense, lazy, useCallback, useState } from 'react'
import { AlertTriangle, Download, FileText, LoaderCircle } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { TextPreview } from './text-preview'
import { useDocumentPreview } from './use-document-preview'
import type { PreviewFailure, PreviewState } from './use-document-preview'
import type { DocumentPreviewTargetInput } from '@/graphql/schema'
import { DocumentPreviewRenderer } from '@/graphql/schema'
import { Button } from '@/shared/ui/button'

const PdfViewer = lazy(() => import('./pdf-viewer'))

export type DocumentPreviewPanelProps = {
  target: DocumentPreviewTargetInput
  fileName: string
  onDownload: () => void
  downloadBusy?: boolean
  downloadError?: string | null
}

function Loading() {
  const { t } = useTranslation('documentPreview')

  return (
    <div
      role="status"
      className="flex size-full items-center justify-center gap-2 text-sm text-muted-foreground"
    >
      <LoaderCircle className="size-5 animate-spin" aria-hidden="true" />
      {t('loading')}
    </div>
  )
}

function Fallback({
  reason,
  onDownload,
  busy,
}: {
  reason: PreviewFailure
  onDownload: () => void
  busy?: boolean
}) {
  const { t } = useTranslation('documentPreview')

  return (
    <div
      role="alert"
      className="flex size-full flex-col items-center justify-center gap-3 p-6 text-center"
    >
      <AlertTriangle
        className="size-8 text-muted-foreground"
        aria-hidden="true"
      />
      <p className="max-w-md text-sm">{t(`failure.${reason}`)}</p>
      <Button type="button" disabled={busy} onClick={onDownload}>
        <Download aria-hidden="true" />
        {t('downloadOriginal')}
      </Button>
    </div>
  )
}

function Content({
  state,
  onFailure,
}: {
  state: Extract<PreviewState, { phase: 'ready' }>
  onFailure: (reason: PreviewFailure) => void
}) {
  const failGeneric = useCallback(() => onFailure('generic'), [onFailure])
  if (state.renderer === DocumentPreviewRenderer.Pdf)
    return (
      <Suspense fallback={<Loading />}>
        <PdfViewer source={state.source} onFailure={onFailure} />
      </Suspense>
    )

  return (
    <TextPreview
      source={state.source}
      renderer={state.renderer}
      onFailure={failGeneric}
    />
  )
}

function failureOf(
  state: PreviewState,
  viewerFailure: { key: string; reason: PreviewFailure } | null,
  key: string,
): PreviewFailure | null {
  if (state.phase === 'failed') return state.reason

  return viewerFailure?.key === key ? viewerFailure.reason : null
}

function Body({
  state,
  failure,
  onFailure,
  onDownload,
  busy,
}: {
  state: PreviewState
  failure: PreviewFailure | null
  onFailure: (reason: PreviewFailure) => void
  onDownload: () => void
  busy?: boolean
}) {
  if (failure)
    return <Fallback reason={failure} onDownload={onDownload} busy={busy} />
  if (state.phase === 'ready')
    return <Content state={state} onFailure={onFailure} />

  return <Loading />
}

/**
 * Shared by the attachment gallery and AI chat: file name, download of the
 * original, loading and fallback states, and the format specific viewer.
 */
export function DocumentPreviewPanel({
  target,
  fileName,
  onDownload,
  downloadBusy,
  downloadError,
}: DocumentPreviewPanelProps) {
  const { t } = useTranslation('documentPreview')
  const state = useDocumentPreview(target)
  const [viewerFailure, setViewerFailure] = useState<{
    key: string
    reason: PreviewFailure
  } | null>(null)
  const key = `${target.kind}:${target.id}`
  const failure = failureOf(state, viewerFailure, key)
  const title = state.phase === 'ready' ? state.fileName : fileName
  const reportFailure = useCallback(
    (reason: PreviewFailure) => setViewerFailure({ key, reason }),
    [key],
  )

  return (
    <div className="flex size-full min-h-0 min-w-0 flex-col">
      <div className="flex items-center gap-2 border-b py-2 pr-12 pl-3">
        <FileText
          className="size-5 shrink-0 text-muted-foreground"
          aria-hidden="true"
        />
        <h2
          className="min-w-0 flex-1 truncate text-sm font-medium"
          title={title}
        >
          {title}
        </h2>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={downloadBusy}
          aria-label={t('downloadFile', { name: title })}
          onClick={onDownload}
        >
          <Download aria-hidden="true" />
          <span className="hidden sm:inline">{t('download')}</span>
        </Button>
      </div>
      {downloadError ? (
        <p role="alert" className="px-3 py-1 text-xs text-destructive">
          {downloadError}
        </p>
      ) : null}
      <div className="min-h-0 flex-1">
        <Body
          state={state}
          failure={failure}
          onFailure={reportFailure}
          onDownload={onDownload}
          busy={downloadBusy}
        />
      </div>
    </div>
  )
}
